import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { TeamReducer } from './types';
import { RequestStatus } from '../../../enums/requestStatus';
import { InvitationDocument } from '../../../models/documents/InvitationDocument';
import { TeamMemberDocument } from '../../../models/documents/TeamMemberDocument';

const initialState: TeamReducer = {
  invitations: [],
  invitationsStatus: null,
  members: [],
  membersStatus: null,
  sendInvitationStatus: null,
  revokeInvitationStatus: null,
  updatePermissionsStatus: null,
  removeMemberStatus: null,
  toggleMemberStatusStatus: null,
  forcePasswordResetStatus: null,
  error: null,
};

export const TEAM_REDUCER = 'Team';

const teamSlice = createSlice({
  name: TEAM_REDUCER,
  initialState,
  reducers: {
    subscribeToInvitationsStarted(state) {
      return {
        ...state,
        invitationsStatus: RequestStatus.SUBSCRIBING,
        error: null,
      };
    },
    subscribeToInvitationsSuccess(state, { payload }: PayloadAction<InvitationDocument[]>) {
      return {
        ...state,
        invitations: payload,
        invitationsStatus: RequestStatus.SUCCESS,
      };
    },
    subscribeToInvitationsFailed(state, { payload }: PayloadAction<string>) {
      return {
        ...state,
        invitationsStatus: RequestStatus.FAILED,
        error: payload,
      };
    },
    unsubscribeFromInvitations(state) {
      state.invitations = [];
      state.invitationsStatus = null;
    },
    subscribeToMembersStarted(state) {
      return {
        ...state,
        membersStatus: RequestStatus.SUBSCRIBING,
        error: null,
      };
    },
    subscribeToMembersSuccess(state, { payload }: PayloadAction<TeamMemberDocument[]>) {
      return {
        ...state,
        members: payload,
        membersStatus: RequestStatus.SUCCESS,
      };
    },
    subscribeToMembersFailed(state, { payload }: PayloadAction<string>) {
      return {
        ...state,
        membersStatus: RequestStatus.FAILED,
        error: payload,
      };
    },
    unsubscribeFromMembers(state) {
      state.members = [];
      state.membersStatus = null;
    },
    sendInvitationStarted(state) {
      return {
        ...state,
        sendInvitationStatus: RequestStatus.UPDATING,
        error: null,
      };
    },
    sendInvitationSuccess(state) {
      return {
        ...state,
        sendInvitationStatus: RequestStatus.SUCCESS,
      };
    },
    sendInvitationFailed(state, { payload }: PayloadAction<string>) {
      return {
        ...state,
        sendInvitationStatus: RequestStatus.FAILED,
        error: payload,
      };
    },
    revokeInvitationStarted(state) {
      return {
        ...state,
        revokeInvitationStatus: RequestStatus.UPDATING,
        error: null,
      };
    },
    revokeInvitationSuccess(state) {
      return {
        ...state,
        revokeInvitationStatus: RequestStatus.SUCCESS,
      };
    },
    revokeInvitationFailed(state, { payload }: PayloadAction<string>) {
      return {
        ...state,
        revokeInvitationStatus: RequestStatus.FAILED,
        error: payload,
      };
    },
    updatePermissionsStarted(state) {
      return {
        ...state,
        updatePermissionsStatus: RequestStatus.UPDATING,
        error: null,
      };
    },
    updatePermissionsSuccess(state) {
      return {
        ...state,
        updatePermissionsStatus: RequestStatus.SUCCESS,
      };
    },
    updatePermissionsFailed(state, { payload }: PayloadAction<string>) {
      return {
        ...state,
        updatePermissionsStatus: RequestStatus.FAILED,
        error: payload,
      };
    },
    removeMemberStarted(state) {
      return {
        ...state,
        removeMemberStatus: RequestStatus.UPDATING,
        error: null,
      };
    },
    removeMemberSuccess(state) {
      return {
        ...state,
        removeMemberStatus: RequestStatus.SUCCESS,
      };
    },
    removeMemberFailed(state, { payload }: PayloadAction<string>) {
      return {
        ...state,
        removeMemberStatus: RequestStatus.FAILED,
        error: payload,
      };
    },
    toggleMemberStatusStarted(state) {
      return {
        ...state,
        toggleMemberStatusStatus: RequestStatus.UPDATING,
        error: null,
      };
    },
    toggleMemberStatusSuccess(state) {
      return {
        ...state,
        toggleMemberStatusStatus: RequestStatus.SUCCESS,
      };
    },
    toggleMemberStatusFailed(state, { payload }: PayloadAction<string>) {
      return {
        ...state,
        toggleMemberStatusStatus: RequestStatus.FAILED,
        error: payload,
      };
    },
    forcePasswordResetStarted(state) {
      return {
        ...state,
        forcePasswordResetStatus: RequestStatus.UPDATING,
        error: null,
      };
    },
    forcePasswordResetSuccess(state) {
      return {
        ...state,
        forcePasswordResetStatus: RequestStatus.SUCCESS,
      };
    },
    forcePasswordResetFailed(state, { payload }: PayloadAction<string>) {
      return {
        ...state,
        forcePasswordResetStatus: RequestStatus.FAILED,
        error: payload,
      };
    },
    resetTeamErrors(state) {
      return {
        ...state,
        error: null,
        sendInvitationStatus: null,
        revokeInvitationStatus: null,
        updatePermissionsStatus: null,
        removeMemberStatus: null,
        toggleMemberStatusStatus: null,
        forcePasswordResetStatus: null,
      };
    },
  },
});

export const {
  subscribeToInvitationsStarted,
  subscribeToInvitationsSuccess,
  subscribeToInvitationsFailed,
  unsubscribeFromInvitations,
  subscribeToMembersStarted,
  subscribeToMembersSuccess,
  subscribeToMembersFailed,
  unsubscribeFromMembers,
  sendInvitationStarted,
  sendInvitationSuccess,
  sendInvitationFailed,
  revokeInvitationStarted,
  revokeInvitationSuccess,
  revokeInvitationFailed,
  updatePermissionsStarted,
  updatePermissionsSuccess,
  updatePermissionsFailed,
  removeMemberStarted,
  removeMemberSuccess,
  removeMemberFailed,
  toggleMemberStatusStarted,
  toggleMemberStatusSuccess,
  toggleMemberStatusFailed,
  forcePasswordResetStarted,
  forcePasswordResetSuccess,
  forcePasswordResetFailed,
  resetTeamErrors,
} = teamSlice.actions;

export default teamSlice.reducer;
